import { useState, useEffect } from 'react';
import { CloudType } from '../../types/alerts';
import { Badge } from '../../../../ui/common-ui/badge';
import { Input } from '../../../../ui/common-ui/input';
import { Clock, User, Shield, Database, X, Search, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { TimeWindowSelector } from './TimeWindowSelector';
import { fetchAlertSelectableItems, SelectableItemsResponse } from '../../services/api';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../../ui/common-ui/collapsible';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../../ui/common-ui/accordion';

interface CreateAlertSelectorsProps {
  tenantName: string;
  cloudId: string;
  selectedCloud: CloudType;
  selectedTimeWindows: string[];
  selectedUsers: string[];
  selectedServiceAccounts: string[];
  selectedResources: string[];
  onTimeWindowsChange: (windows: string[]) => void;
  onUsersChange: (users: string[]) => void;
  onServiceAccountsChange: (accounts: string[]) => void;
  onResourcesChange: (resources: string[]) => void;
  disabled?: boolean;
}

export function CreateAlertSelectors({
  tenantName,
  cloudId,
  selectedCloud,
  selectedTimeWindows,
  selectedUsers,
  selectedServiceAccounts,
  selectedResources,
  onTimeWindowsChange,
  onUsersChange,
  onServiceAccountsChange,
  onResourcesChange,
  disabled = false,
}: CreateAlertSelectorsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectableItems, setSelectableItems] = useState<SelectableItemsResponse | null>(null);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [saSearch, setSaSearch] = useState('');
  const [resourceSearch, setResourceSearch] = useState('');

  // Fetch selectable items when expanded
  useEffect(() => {
    if (isExpanded && tenantName && cloudId && !selectableItems) {
      setIsLoadingItems(true);
      fetchAlertSelectableItems(tenantName, cloudId, selectedCloud)
        .then(setSelectableItems)
        .catch(console.error)
        .finally(() => setIsLoadingItems(false));
    }
  }, [isExpanded, tenantName, cloudId, selectedCloud, selectableItems]);

  // Reset selectable items when cloud/resource changes
  useEffect(() => {
    setSelectableItems(null);
  }, [tenantName, cloudId, selectedCloud]);

  const totalSelected = selectedTimeWindows.length + selectedUsers.length + selectedServiceAccounts.length + selectedResources.length;

  const getServiceAccountLabel = () => {
    return selectedCloud === 'AWS' ? 'IAM Roles' : 'Service Accounts';
  };

  const getServiceAccountItems = (): string[] => {
    if (!selectableItems) return [];
    if (selectedCloud === 'AWS') {
      return selectableItems.awsIamRole || [];
    }
    return selectableItems.serviceAccount || selectableItems.serviceAccounts || [];
  };

  const toggleUser = (user: string) => {
    if (selectedUsers.includes(user)) {
      onUsersChange(selectedUsers.filter(u => u !== user));
    } else {
      onUsersChange([...selectedUsers, user]);
    }
  };

  const toggleServiceAccount = (sa: string) => {
    if (selectedServiceAccounts.includes(sa)) {
      onServiceAccountsChange(selectedServiceAccounts.filter(s => s !== sa));
    } else {
      onServiceAccountsChange([...selectedServiceAccounts, sa]);
    }
  };

  const toggleResource = (resource: string) => {
    if (selectedResources.includes(resource)) {
      onResourcesChange(selectedResources.filter(r => r !== resource));
    } else {
      onResourcesChange([...selectedResources, resource]);
    }
  };

  const users = selectableItems?.users || [];
  const serviceAccounts = getServiceAccountItems();
  const resources = selectableItems?.resources || {};

  const filteredUsers = users.filter(u => u.toLowerCase().includes(userSearch.toLowerCase()));
  const filteredSAs = serviceAccounts.filter(sa => sa.toLowerCase().includes(saSearch.toLowerCase()));

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <CollapsibleTrigger asChild disabled={disabled}>
        <button
          className={`
            w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all duration-200
            ${isExpanded 
              ? 'bg-card border-primary/30 shadow-lg shadow-sky-500/5' 
              : 'bg-card backdrop-blur-xl shadow-lg shadow-sky-500/5 border-border/50 hover:border-primary/30 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)]'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Plus className="h-4 w-4 text-primary" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium">Pre-configure Alert Parameters</p>
              <p className="text-xs text-muted-foreground">
                {totalSelected > 0 
                  ? `${totalSelected} item${totalSelected !== 1 ? 's' : ''} selected`
                  : 'Optional: Set time windows, users, resources before generating'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {totalSelected > 0 && (
              <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                {totalSelected}
              </Badge>
            )}
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-3">
        <div className="p-4 rounded-2xl border portal-card-elevated bg-card space-y-4">
          {/* Selected Items Summary */}
          {totalSelected > 0 && (
            <div className="flex flex-wrap gap-2 pb-3 border-b border-border">
              {selectedTimeWindows.map(tw => (
                <Badge key={tw} variant="secondary" className="gap-1 pr-1 bg-primary/10 text-primary border-primary/20">
                  <Clock className="h-3 w-3" />
                  {tw}
                  <button onClick={() => onTimeWindowsChange(selectedTimeWindows.filter(t => t !== tw))} className="ml-1 p-0.5 rounded-full hover:bg-primary/20">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {selectedUsers.map(user => (
                <Badge key={user} variant="secondary" className="gap-1 pr-1 bg-blue-500/10 text-blue-600 border-blue-500/20">
                  <User className="h-3 w-3" />
                  <span className="max-w-[150px] truncate">{user}</span>
                  <button onClick={() => onUsersChange(selectedUsers.filter(u => u !== user))} className="ml-1 p-0.5 rounded-full hover:bg-blue-500/20">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {selectedServiceAccounts.map(sa => (
                <Badge key={sa} variant="secondary" className="gap-1 pr-1 bg-amber-500/10 text-amber-600 border-amber-500/20">
                  <Shield className="h-3 w-3" />
                  <span className="max-w-[150px] truncate">{sa}</span>
                  <button onClick={() => onServiceAccountsChange(selectedServiceAccounts.filter(s => s !== sa))} className="ml-1 p-0.5 rounded-full hover:bg-amber-500/20">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {selectedResources.map(res => (
                <Badge key={res} variant="secondary" className="gap-1 pr-1 bg-green-500/10 text-green-600 border-green-500/20">
                  <Database className="h-3 w-3" />
                  <span className="max-w-[150px] truncate">{res}</span>
                  <button onClick={() => onResourcesChange(selectedResources.filter(r => r !== res))} className="ml-1 p-0.5 rounded-full hover:bg-green-500/20">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Time Window Selector */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Time Windows</span>
            </div>
            <TimeWindowSelector
              selectedWindows={selectedTimeWindows}
              onWindowsChange={onTimeWindowsChange}
            />
          </div>

          {isLoadingItems ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : (
            <>
              {/* Users */}
              {users.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium">Users</span>
                    {selectedUsers.length > 0 && (
                      <Badge variant="secondary" className="text-xs">{selectedUsers.length}</Badge>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search users..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-8 h-8 text-sm"
                    />
                  </div>
                  <div className="rounded-lg border border-border bg-muted/20 p-2">
                    <div className="max-h-[160px] overflow-y-auto space-y-1 scrollbar-thin">
                      {filteredUsers.map(user => {
                        const isSelected = selectedUsers.includes(user);
                        return (
                          <button
                            key={user}
                            onClick={() => toggleUser(user)}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                              isSelected 
                                ? 'bg-primary/10 text-primary border border-primary/30' 
                                : 'hover:bg-muted/50 border border-transparent'
                            }`}
                          >
                            <span className="truncate block">{user}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Service Accounts / IAM Roles */}
              {serviceAccounts.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium">{getServiceAccountLabel()}</span>
                    {selectedServiceAccounts.length > 0 && (
                      <Badge variant="secondary" className="text-xs">{selectedServiceAccounts.length}</Badge>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder={`Search ${getServiceAccountLabel().toLowerCase()}...`}
                      value={saSearch}
                      onChange={(e) => setSaSearch(e.target.value)}
                      className="pl-8 h-8 text-sm"
                    />
                  </div>
                  <div className="rounded-lg border border-border bg-muted/20 p-2">
                    <div className="max-h-[160px] overflow-y-auto space-y-1 scrollbar-thin">
                      {filteredSAs.map(sa => {
                        const isSelected = selectedServiceAccounts.includes(sa);
                        return (
                          <button
                            key={sa}
                            onClick={() => toggleServiceAccount(sa)}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                              isSelected 
                                ? 'bg-primary/10 text-primary border border-primary/30' 
                                : 'hover:bg-muted/50 border border-transparent'
                            }`}
                          >
                            <span className="truncate block">{sa}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Resources */}
              {Object.keys(resources).length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Database className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium">Resources</span>
                    {selectedResources.length > 0 && (
                      <Badge variant="secondary" className="text-xs">{selectedResources.length}</Badge>
                    )}
                  </div>
                  <div className="relative mb-2">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search resources..."
                      value={resourceSearch}
                      onChange={(e) => setResourceSearch(e.target.value)}
                      className="pl-8 h-8 text-sm"
                    />
                  </div>
                  <div className="max-h-[240px] overflow-y-auto scrollbar-thin">
                      <Accordion type="multiple" className="space-y-1">
                        {Object.entries(resources).map(([type, items]) => {
                          const filteredItems = items.filter(item => 
                            item.toLowerCase().includes(resourceSearch.toLowerCase())
                          );
                          const selectedCount = items.filter(r => selectedResources.includes(r)).length;
                          if (filteredItems.length === 0) return null;

                          return (
                            <AccordionItem key={type} value={type} className="border rounded-lg px-3 bg-background">
                              <AccordionTrigger className="py-2 text-sm hover:no-underline">
                                <div className="flex items-center gap-2">
                                  <span className="capitalize">{type.replace(/-/g, ' ')}</span>
                                  {selectedCount > 0 && (
                                    <Badge variant="secondary" className="text-xs bg-green-500/10 text-green-600">
                                      {selectedCount}
                                    </Badge>
                                  )}
                                </div>
                              </AccordionTrigger>
                              <AccordionContent>
                                <div className="rounded-lg border border-border bg-muted/20 p-2 mt-1">
                                  <div className="max-h-[120px] overflow-y-auto space-y-1 scrollbar-thin">
                                    {filteredItems.map(item => {
                                      const isSelected = selectedResources.includes(item);
                                      return (
                                        <button
                                          key={item}
                                          onClick={() => toggleResource(item)}
                                          className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                                            isSelected 
                                              ? 'bg-green-500/10 text-green-600 border border-green-500/30' 
                                              : 'hover:bg-muted/50 border border-transparent'
                                          }`}
                                        >
                                          <span className="truncate block">{item}</span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    </div>
                </div>
              )}
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
